<template>
  <div>
    <v-layout class="title" align-baseline justify-space-between>
      <v-flex xs1>
        {{ $t('labelDetails') }}:
      </v-flex>
      <v-flex md2>
        <v-autocomplete
          hide-details
          :items="templates"
          return-object
          :label="$t('template')"
          item-text="name"
          @change="onSelectTemplate"></v-autocomplete>
      </v-flex>
      <v-flex xs1>
        <v-menu
          :close-on-content-click="false"
          v-model="shipDateMenu"
          :nudge-right="40"
          lazy
          transition="scale-transition"
          offset-y
          full-width
          min-width="290px"
        >
          <template v-slot:activator="data">
            <v-text-field
              hide-details
              :value="shipDate"
              :label="$t('shipDate')"
              readonly
              v-on="data.on"
            ></v-text-field>
          </template>
          <v-date-picker v-model="shipDate" @input="shipDateMenu = false"></v-date-picker>
        </v-menu>
      </v-flex>
      <v-flex xs3>
        <v-autocomplete
          v-if="autoGetRate"
          v-model="carriers"
          :items="allCarriers"
          :label="$t('autoGetRate')"
          :rules="[arrayLengthRule()]"
          multiple
         >
         <template v-slot:prepend-item>
          <v-list-tile
            ripple
            @mousedown.prevent
            @click="selectAll"
          >
            <v-list-tile-action>
              <v-icon color="#224855">{{ allIcon }}</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                {{ $t('selectAll') }}
              </v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider class="mt-2"></v-divider>
          </template>
          <template v-slot:append>
            <v-checkbox
            :style="{height: '10px', marginTop: '-10px'}"
            v-model="autoGetRate"
            :label="$t('autoGetRate')"
            ></v-checkbox>
          </template>
        </v-autocomplete>
        <v-autocomplete
          v-else
          hide-details
          :items="filterShippingServiceByFromState(shippingServices, fromState)"
          item-text="text"
          item-value="value"
          :menu-props="{returnValue:'value'}"
          v-model="shippingService"
          :rules="[fieldIsRequired('service'), checkShipForm()]"
          :label="$t('shippingService')"
        >
          <template v-slot:item="data">
            <template v-if="isObject(data.item)">
              <v-list-tile-content v-text="data.item"></v-list-tile-content>
            </template>
            <template v-else>
              <v-list-tile-content>
                <v-list-tile-title>
                  {{data.item.text}}
                </v-list-tile-title>
              </v-list-tile-content>
            </template>
          </template>
          <template v-slot:append>
            <v-checkbox
            :style="{height: '10px', marginTop: '-10px'}"
            v-model="autoGetRate"
            :label="$t('autoGetRate')"
            ></v-checkbox>
          </template>

        </v-autocomplete>
      </v-flex>
      <v-flex xs2>
        <v-select
          class="table-item"
          hide-details
          :label="$t('signature')"
          :items="signatureOptions"
          item-text="text"
          :menu-props="{returnValue:'value'}"
          v-model="signature"/>
      </v-flex>
      <v-flex xs2>
        <v-select
          class="table-item"
          hide-details
          :label="$t('labelStockType')"
          :items="labelStockTypes"
          item-text="text"
          :menu-props="{returnValue:'value'}"
          :disabled="!isSupportLabelStockType"
          v-model="labelStockType"/>
      </v-flex>
    </v-layout>
    <v-layout justify-space-between>
      <v-flex md4>
        <v-text-field
          :label="$t('note')"
          :hint="$t('noteWillNotBePrintedOnLabel')"
          v-model.trim="note"
          :rules="[fieldCharLimit('Note', 100)]"
          counter="100"></v-text-field>
      </v-flex>
      <v-flex md4>
        <v-text-field
          :label="$t('customOrderId')"
          v-model.trim="customOrderId">
        </v-text-field>
      </v-flex>
      <v-flex md3>
        <v-text-field
          :label="$t('memo')"
          :hint="$t('memoWillBePrintedOnLabel')"
          :rules="[fieldCharLimit('Memo', currentShippingService.memoLength || 40)]"
          v-model.trim="memo"
          :counter="currentShippingService.memoLength || 40">
        </v-text-field>
      </v-flex>
    </v-layout>
    <V-layout v-if="isSupportHoldAtLocation || isSupportSaturdayDelivery">
      <v-flex md2 v-if="isSupportSaturdayDelivery">
        <v-checkbox
          v-model="isSaturdayDelivery"
          :label="$t('supportSaturdayDelivery')"
        ></v-checkbox>
      </v-flex>
      <v-flex md2>
        <v-checkbox
          :label="$t('supportHoldAtLocation')"
          @change="holdAtLocationChange"
          v-model="isHoldAtLocation"
        />
      </v-flex>
      <v-flex md3 v-if="isHoldAtLocation">
        <v-select
          hide-details
          item-text="name"
          item-value="locationId"
          :label="$t('location')"
          :items="locations"
          content-class="location-items"
          :menu-props="{returnValue:'value'}"
          v-model="location"
          :rules="[
            fieldIsRequired('location'),
          ]"
        >
         <template v-slot:item="{item}">
            <div>
              {{ item.address.companyName }}
            </div>
            <div>
              {{ [item.address.address1, item.address.address2, item.address.city, item.address.state, item.address.zipCode].filter(item => item).join(', ') }}
            </div>
            <div>
              {{ `${$t('distance')}: ${item.distance.value} ${item.distance.units}` }}
            </div>
            <div :key="index" v-for="(row, index) in item.storeHours">
              {{ row.days.join(',') }} {{ row.openTime }} - {{ row.closeTime }}
            </div>
            <div v-if="item.locationAttributeTypes">{{ $t('locationTypes') }}</div>
            <div :key="row.locationId" v-for="(row) in item.locationAttributeTypes">
              · {{ formatLocationAttribute(row) }}
            </div>
         </template>
        </v-select>
      </v-flex>
      <v-flex md7></v-flex>
    </V-layout>
    <v-data-table
      :key="currentShippingService.isOneRate ? 'ONE_RATE' : 'STANDARD_RATE'"
      :headers="headers"
      :items="packages"
      hide-actions
      class="elevation-2 mt-2"
      item-key="_key"
      :expand="true"
      caption
    >
      <template v-slot:headerCell="props">
        <span v-if="props.header.value === 'action'">
          <v-tooltip top>
            <template v-slot:activator="tooltip">
              <v-btn v-on="tooltip.on" color="primary" flat small icon @click="addPackage" :disabled="!serviceSetting.enableMWT"><v-icon>add</v-icon></v-btn>
            </template>
            {{ $t('supportUpToMaxPkgQtyPackages', { maxPkgQty: serviceSetting.maxPkgQty }) }}
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="tooltip">
              <v-btn v-on="tooltip.on" flat small icon color="primary"  @click=" () => {isOpenImportPackagesDialog = true}" :disabled="!serviceSetting.enableMWT">
                <v-icon>cloud_upload</v-icon>
              </v-btn>
            </template>
            {{ $t('uploadPackageDimensions') }}
          </v-tooltip>
        </span>
        <v-tooltip top v-else-if="props.header.value === 'weight' && !isMeasurementMetric" >
          <template v-slot:activator="{ on }">
            <span v-on="on" @click="isOz = !isOz">{{ props.header.text }}</span>
          </template>
          {{ $t('clickHereSwitchTo') + (isOz ? 'Lbs' : 'Oz') }}
        </v-tooltip>
        <template v-else-if="props.header.value === 'reference'">
          <v-select
            hide-details
            :label="$t('reference')"
            :items="referenceOptions"
            item-text="text"
            :menu-props="{returnValue:'value'}"
            v-model="reference"
          />
        </template>
        <span v-else>{{ props.header.text }}</span>
      </template>
      <template v-slot:items="props">
        <td class="text-xs-center">
          <v-btn v-if="props.index > 0" color="primary" flat small icon @click="deletePackage(props.item)"><v-icon>delete</v-icon> </v-btn>
        </td>
        <td class="text-xs-left">
          <v-text-field
            class="table-item"
            hide-details
            v-model.number="props.item.qty"
            :disabled="!serviceSetting.enableMWT"
            :rules="[
              fieldIsRequired('qty'),
              fieldIsOverZero('qty'),
              fieldIsInteger('qty')
            ]"></v-text-field>
        </td>
        <td class="text-xs-left" v-if="currentShippingService.isOneRate">
          <v-layout align-center>
            <vite-autocomplete
              :rules="[
                fieldIsRequired('packagingType')
              ]"
              class="table-item"
              hide-details
              :items="fedexPackagings"
              v-model="packageType"
              item-text="name"
              item-value="id"
            ></vite-autocomplete>
          </v-layout>
        </td>
        <template v-else>
          <td class="text-xs-left">
            <v-text-field
              class="table-item"
              hide-details
              v-model.number="props.item.weightLb"
              :rules="[
                fieldIsRequired('weight'),
                fieldIsNoLessThanZero('weight'),
                isMeasurementMetric ? (() => true) : fieldIsPkgWeight(isOz)
              ]"
              :suffix="isMeasurementMetric ? 'kg' : (isOz ? 'Oz' : 'Lbs')"></v-text-field>
          </td>
          <td class="text-xs-left">
            <v-layout align-center>
              <v-flex xs4 class="mr-3">
                <v-text-field
                  class="table-item"
                  hide-details
                  :readonly="currentShippingService.isOneRate"
                  v-model.number="props.item.length"
                  :suffix="sizeSuffix"
                  :rules="[
                    fieldIsRequired('length'),
                    fieldIsOverZero('length')
                  ]"></v-text-field>
              </v-flex>
              <v-flex xs4 class="mr-3">
                <v-text-field
                  class="table-item"
                  hide-details
                  :readonly="currentShippingService.isOneRate"
                  v-model.number="props.item.width"
                  :suffix="sizeSuffix"
                  :rules="[
                    fieldIsRequired('width'),
                    fieldIsOverZero('width')
                  ]"></v-text-field>
              </v-flex>
              <v-flex xs4>
                <v-text-field
                  class="table-item"
                  hide-details
                  :readonly="currentShippingService.isOneRate"
                  v-model.number="props.item.height"
                  :suffix="sizeSuffix"
                  :rules="[
                    fieldIsRequired('height'),
                    fieldIsOverZero('height')
                  ]"></v-text-field>
              </v-flex>
              <v-flex xs1>
                <PackagingSelector
                  :packagings="packagings"
                  @select="item => onSelectPackaging(item, props.item)"
                />
              </v-flex>
            </v-layout>
          </td>
        </template>
        <td class="text-xs-left">
          <v-text-field
            class="table-item"
            hide-details
            :disabled="!serviceSetting.enableInsured"
            v-model.number="props.item.insuredValue"
            @change="(e) => defaultZero(e, props.item)"
            :rules="[fieldIsRequired('value'), fieldIsNoLessThanZero('value')]"></v-text-field>
        </td>
        <td class="text-xs-left">
          <v-text-field
            class="table-item"
            hide-details
            :disabled="serviceSetting.disabledPkgMemo"
            :rules="[fieldCharLimit('Memo', currentShippingService.memoLength || 40)]"
            v-model="props.item.memo"></v-text-field>
        </td>
        <td class="text-xs-left" v-if="currentShippingService.enableReferences">
          <v-text-field
            class="table-item"
            hide-details
            :rules="[fieldCharLimit('Reference', currentShippingService.referenceLength || 40)]"
            v-model="props.item.reference"></v-text-field>
        </td>
        <td class="text-xs-center" v-if="isLithiumBatteryDangerousGoods">
          <v-layout align-center style="cursor: pointer;" justify-center :id="`expand-icon-${props.item._key || props.index}`" @click="toggleExpand(props)">
            <v-icon color="primary" class="mr-1">battery_charging_full</v-icon>
            <v-icon>expand_more</v-icon>
          </v-layout>
        </td>
      </template>
      <template v-slot:expand="props">
        <LithiumBatteryDangerousGoods
          v-if="isLithiumBatteryDangerousGoods || (props.item.isLithiumBattery && props.item.lithiumBatteryOptions && props.item.lithiumBatteryOptions.length > 0)"
          :props="props"
          :lithiumBatteryOptionItems="lithiumBatteryOptionItems"
          @applyAll="applyAllLithiumBattery"
        />
      </template>
    </v-data-table>
    <ImportPackagesDimensions
      v-model="isOpenImportPackagesDialog"
      :isShow="isOpenImportPackagesDialog"
      @confirmMethod="loadDimensionsData"
    >
    </ImportPackagesDimensions>
  </div>
</template>

<script>
import { cloneDeep, checkRules, labelServiceComponentTools, timeTools, measurementTools, isObject } from '@/utils/tools'
import ImportPackagesDimensions from './ImportPackagesDimensions'
import { PRICING_OPTIONS } from '@/utils/const'
import PackagingSelector from './PackagingSelector'
import LithiumBatteryDangerousGoods from './LithiumBatteryDangerousGoods.vue'

const METAL_CONTAINED = 'METAL_CONTAINED'
const METAL_PACKED = 'METAL_PACKED'
const ION_CONTAINED = 'ION_CONTAINED'
const ION_PACKED = 'ION_PACKED'

const initPackage = {
  height: 0,
  width: 0,
  weightLb: 0,
  length: 0,
  isOz: false,
  insuredValue: 0,
  packaging: 'Default packaging',
  qty: 1,
  memo: '',
  pieces: 1,
  isLithiumBattery: false,
  lithiumBatteryOptions: []
}

export default {
  name: 'LabelPackageWidget',
  data () {
    return {
      memo: '',
      note: '',
      shipDate: this.toPickerDateString(new Date(new Date().getHours() >= 20 ? Date.now() + 86400000 : Date.now())),
      shipDateMenu: false,
      isOz: false,
      shippingService: '',
      signature: '',
      isSaturdayDelivery: this.initIsSaturdayDelivery,
      labelStockType: this.initLabelStockType || 'STOCK_4X6',
      isOpenImportPackagesDialog: false,
      carriers: [],
      allCarriers: ['UPS', 'USPS', 'FEDEX'],
      autoGetRate: false,
      isAutoGetRate: '',
      reference: '',
      packageType: '',
      customOrderId: this.order?.orderId || '',
      forceExpandState: null,
      locations: [],
      location: '',
      isHoldAtLocation: false
    }
  },
  components: {
    ImportPackagesDimensions,
    PackagingSelector,
    LithiumBatteryDangerousGoods
  },
  mixins: [checkRules, timeTools, measurementTools, labelServiceComponentTools],
  mounted () {
    let defaultShippingService = (this.shippingServices.find((serviceType) => serviceType.default) || {}).value || 'FEDEX_GROUND'
    const {note, memo, signature, packageType} = this.noteMemoSignature
    const shippingService = this.$attrs.shippingService
    if (shippingService) defaultShippingService = shippingService
    this.setShippingService(defaultShippingService)
    this.setCarrier()
    this.$emit('update:packages', [cloneDeep(initPackage)])
    if (note || memo || signature || packageType) Object.assign(this, this.noteMemoSignature)
  },
  computed: {
    referenceOptions () {
      return [
        { text: 'department number', value: 'dept' },
        { text: 'invoice number', value: 'inv' },
        { text: 'po number', value: 'po' }
      ]
    },
    packageStat () {
      return this.packages.reduce((stat, pkg) => ({
        totalQty: stat.totalQty + pkg.qty,
        totalWeight: Math.round((stat.totalWeight + pkg.weightLb * pkg.qty) * 100) / 100
      }), {
        totalQty: 0,
        totalWeight: 0
      })
    },
    headers () {
      const { isOneRate, enableReferences } = this.currentShippingService
      const headers = [
        { text: 'Action', value: 'action', align: 'center', sortable: false },
        { text: `${this.$t('packageQty')} (${this.packageStat.totalQty})`, value: '', align: 'left', sortable: false },
        this.isMeasurementMetric ?
          { text: `${this.$t('weight')}(${this.packageStat.totalWeight}kg)`, value: 'weight', align: 'left', sortable: false } :
          { text: `${this.$t('weight')}(${this.packageStat.totalWeight}${this.isOz ? 'Oz' : 'Lbs'})`, value: 'weight', align: 'left', sortable: false },
        { text: `${this.$t('length')} / ${this.$t('width')} / ${this.$t('height')}`, value: 'packaging', align: 'left', sortable: false, width: '18%' },
        { text: this.$t('declaredValue'), value: 'createTime', align: 'left', sortable: false },
        { text: this.$t('memo'), value: 'memo', align: 'left', sortable: false, width: '22%' }
      ]
      if (isOneRate) {
        headers.splice(2, 2, { text: `${this.$t('packagingType')}`, value: 'packageType', align: 'left', sortable: false })
      }
      if (enableReferences) {
        headers.push(
          { text: this.$t('memo'), value: 'reference', align: 'left', sortable: false, width: 300 }
        )
      }
      // Add expand column for lithium battery dangerous goods
      if (this.isLithiumBatteryDangerousGoods) {
        headers.push(
          { text: '', value: '', align: 'center', sortable: false, width: '80px' }
        )
      }
      return headers
    },
    serviceSetting () {
      return this.$store.getters.getLabelServiceSettingByCarrierAndServiceTypeValue(this.carrier, this.shippingService)
    },
    signatureOptions () {
      return this.autoGetRate
        ? [
          {
            text: 'No signature',
            value: 'NO_SIGNATURE_REQUIRED'
          },
          {
            text: 'Direct',
            value: 'DIRECT'
          },
          {
            text: 'Adult',
            value: 'ADULT'
          }
          // {
          //   text: 'Indirect',
          //   value: 'INDIRECT'
          // }
        ]
        : this.serviceSetting.signatureOptions || []
    },
    packagings () {
      return this.currentShippingService?.isOneRate
        ? this.fedexPackagings
        : this.$store.getters.tenantPackagingsWithWarehousePackagings
    },
    fedexPackagings () {
      const packageTypeOptions = this.currentShippingService?.packageTypeOptions
      const packageTypes = this.$store.getters.fedexPackagings
      return packageTypeOptions ? packageTypes.filter(item => packageTypeOptions.includes(item.id)) : packageTypes
    },
    templates () {
      return this.$store.getters.templates
    },
    labelServices () {
      return this.$store.getters.labelServices
    },
    labelStockTypes () {
      return this.$store.getters.labelStockTypes
    },
    shippingServices () {
      return this.$store.getters.shippingServices
    },
    carrier () {
      return (this.labelServices.find(service =>
        service.serviceTypes.find(serviceType =>
          this.shippingService === serviceType.value ||
          this.serviceType === service.carrier
        )
      ) || {}).carrier
    },
    flexibleVars () {
      return this.$store.getters.labelTemplateFlexibleVars
    },
    isMeasurementMetric () {
      return !!this.$store.getters.isMeasurementMetric
    },
    sizeSuffix () {
      return this.isMeasurementMetric ? 'cm' : '"'
    },
    isSupportSaturdayDelivery () {
      return this.labelServices.some(service => {
        const labelService = service.serviceTypes.find(type => type.value === this.shippingService)
        return labelService
          ? labelService.hasOwnProperty('isSaturdayDelivery')
            ? labelService.isSaturdayDelivery
            : service.isSaturdayDelivery
          : false
      })
    },
    isSupportHoldAtLocation () {
      return this.labelServices.some(service => {
        const labelService = service.serviceTypes.find(type => type.value === this.shippingService)
        return labelService
          ? labelService.hasOwnProperty('isSupportHoldAtLocation')
            ? labelService.isSupportHoldAtLocation
            : service.isSupportHoldAtLocation
          : false
      })
    },
    currentShippingService () {
      return this.labelServices.find(service =>
        service.serviceTypes.some(serviceType => this.shippingService === serviceType.value)
      ) || {}
    },
    isSupportLabelStockType () {
      return this.currentShippingService.isSupportLabelStockType
    },
    allIcon () {
      if (this.carriers.length === this.allCarriers.length) return 'check_box'
      else if (this.carriers.length > 0) return 'indeterminate_check_box'
      return 'check_box_outline_blank'
    },
    isLithiumBatteryDangerousGoods () {
      return this.dangerousGoods === 'LITHIUM_BATTERY'
    },
    lithiumBatteryOptionItems () {
      return [
        {
          value: METAL_CONTAINED,
          label: this.$t('metalContainedInEquipment'),
          tooltip: this.$t('metalContainedTooltip')
        },
        {
          value: METAL_PACKED,
          label: this.$t('metalPackedWithEquipment'),
          tooltip: this.$t('metalPackedTooltip')
        },
        {
          value: ION_CONTAINED,
          label: this.$t('ionContainedInEquipment'),
          tooltip: this.$t('ionContainedTooltip')
        },
        {
          value: ION_PACKED,
          label: this.$t('ionPackedWithEquipment'),
          tooltip: this.$t('ionPackedTooltip')
        }
      ]
    }
  },
  watch: {
    packages (value) {
      const targetPkg = value.find(pkg => pkg.po || pkg.dept || pkg.inv)
      if (targetPkg) {
        const reference = this.referenceOptions.find(option => targetPkg[option.value])?.value || ''
        this.reference = reference
      }
      // Initialize _key and lithium battery fields on packages
      value.forEach((pkg, index) => {
        if (!pkg._key) {
          this.$set(pkg, '_key', `pkg_${Date.now()}_${index}`)
        }
        if (pkg.isLithiumBattery === undefined) {
          this.$set(pkg, 'isLithiumBattery', false)
        }
        if (!pkg.lithiumBatteryOptions) {
          this.$set(pkg, 'lithiumBatteryOptions', [])
        }
      })
    },
    signatureOptions (value) {
      let defaultValue = value[0]?.value || ''
      for (let option of value) {
        if (option.default) defaultValue = option.value
        if (option.value === this.signature) return
      }
      this.signature = defaultValue
    },
    shippingService (value, oldValue) {
      if (value === oldValue) return
      this.$emit('update:shippingService', value)
      if (!this.isSupportLabelStockType) this.labelStockType = 'STOCK_4X6'
      this.setCarrier()
      const isSupportDangerousGoods = this.labelServices.some(service => {
        const labelService = service.serviceTypes.find(type => type.value === this.shippingService)
        return labelService
          ? labelService.hasOwnProperty('isSupportDangerousGoods')
            ? labelService.isSupportDangerousGoods
            : service.isSupportDangerousGoods
          : false
      })
      if (!isSupportDangerousGoods) {
        this.$emit('clearDangerousGoods')
        this.packages.forEach(pkg => {
          pkg.isLithiumBattery = false
          pkg.lithiumBatteryOptions = []
        })
      }
    },
    carriers (value) {
      this.$emit('update:carriers', value)
    },
    autoGetRate (value) {
      this.signature = ''
      if (value) {
        this.carriers = this.allCarriers.map(item => item)
      } else {
        this.carriers = []
      }
    },
    'to.city' (newVal, oldVal) {
      if (newVal !== oldVal && this.isHoldAtLocation) {
        this.isHoldAtLocation = false
        this.locations = []
        this.location = ''
      }
    },
    'to.state' (newVal, oldVal) {
      if (newVal !== oldVal && this.isHoldAtLocation) {
        this.isHoldAtLocation = false
        this.locations = []
        this.location = ''
      }
    },
    'to.zipCode' (newVal, oldVal) {
      if (newVal !== oldVal && this.isHoldAtLocation) {
        this.isHoldAtLocation = false
        this.locations = []
        this.location = ''
      }
    },
    dangerousGoods: {
      handler (newVal, oldVal) {
        if (newVal === oldVal) return
        // Auto-expand/collapse rows when switching dangerous goods type
        if (newVal === 'LITHIUM_BATTERY') {
          this.forceExpandState = 'expand'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        } else if (oldVal === 'LITHIUM_BATTERY' && newVal !== 'LITHIUM_BATTERY') {
          this.forceExpandState = 'collapse'
          this.$nextTick(() => {
            this.$nextTick(() => {
              this.packages.forEach((pkg, index) => {
                pkg.isLithiumBattery = false
                pkg.lithiumBatteryOptions = []
                const iconId = `expand-icon-${pkg._key || index}`
                const icon = document.getElementById(iconId)
                if (icon) icon.click()
              })
              this.$nextTick(() => {
                this.forceExpandState = null
              })
            })
          })
        }
        // Initialize lithium battery fields and _key on packages
        this.packages.forEach((pkg, index) => {
          // Initialize _key if not exists
          if (!pkg._key) {
            this.$set(pkg, '_key', `pkg_${Date.now()}_${index}`)
          }
          // Initialize lithium battery fields
          if (pkg.isLithiumBattery === undefined) {
            this.$set(pkg, 'isLithiumBattery', false)
          }
          if (!pkg.lithiumBatteryOptions) {
            this.$set(pkg, 'lithiumBatteryOptions', [])
          }
        })
      },
      immediate: true
    }
  },
  methods: {
    applyAllLithiumBattery (item) {
      this.packages.forEach(pkg => {
        this.$set(pkg, 'lithiumBatteryOptions', [...item.lithiumBatteryOptions])
        this.$set(pkg, 'isLithiumBattery', item.isLithiumBattery)
      })
    },
    formatLocationAttribute (data) {
      const result = data.replace(/_/g, ' ').toLowerCase()
      return result.charAt(0).toUpperCase() + result.slice(1)
    },
    async holdAtLocationChange (value) {
      if (value) {
        if (!this.to.state || !this.to.zipCode || !this.to.city) {
          alert(this.$t('holdAtLocationAlert'))
          this.isHoldAtLocation = false
          return
        }
        try {
          const res = await this.$store.dispatch('getLocations', {
            address: this.to
          })
          this.locations = res.data.locations
          this.location = res.data.matchLocation.locationId
        } catch (err) {
          let msg = err.message
          if (msg === 'GENERIC.ERROR') {
            msg = 'No locations'
          }
          alert(msg)
          this.isHoldAtLocation = false
        }
      } else {
        this.locations = []
        this.location = ''
      }
    },
    isObject,
    loadDimensionsData (data) {
      if (data.length === 0) return
      let arrayData = []
      for (let i = 0; i < data.length; i++) {
        let element = data[i]
        let entity = {
          _key: `pkg_${Date.now()}_${i}_${Math.random().toString(36).substr(2, 9)}`,
          qty: element.Quantity,
          weightLb: element.Weight,
          height: element.Height,
          width: element.Width,
          memo: element.Memo || '',
          length: element.Length,
          insuredValue: (element.DeclaredValue || 0),
          isLithiumBattery: false,
          lithiumBatteryOptions: []
        }
        arrayData.push(entity)
      }
      this.$emit('update:packages', [...arrayData])
    },
    addPackage () {
      const newPackage = cloneDeep(initPackage)
      newPackage._key = `pkg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      newPackage.isLithiumBattery = false
      newPackage.lithiumBatteryOptions = []
      this.$emit('update:packages', [...this.packages, newPackage])
    },
    toggleExpand (props) {
      if (this.forceExpandState === 'expand') {
        props.expanded = true
      } else if (this.forceExpandState === 'collapse') {
        props.expanded = false
      } else {
        props.expanded = !props.expanded
      }
    },
    onSelectPackaging (e, item) {
      const {height, length, width, weight, isMeasurementMetric = false} = e
      Object.assign(item, this.convertPackagingSize({height, length, width}, isMeasurementMetric))
      if (weight) {
        this.$set(item, 'weightLb', this.convertWeight(weight, isMeasurementMetric))
      }
    },
    loadPackagesFromOldFormat (template) {
      const {
        packaging,
        insuredValue,
        weightLb,
        length: templateLength,
        width: templateWidth,
        height: templateHeight,
        isMeasurementMetric: isWeightMeasurementMetric = false
      } = template

      const targetPackaging = this.packagings.find(item => item.id === packaging) || {}
      const {
        height: pkgHeight,
        length: pkgLength,
        width: pkgWidth,
        isMeasurementMetric: isPackagingMeasurementMetric = false,
        ...rest
      } = targetPackaging

      return [{
        ...this.convertPackagingSize({
          height: pkgHeight || templateHeight,
          length: pkgLength || templateLength,
          width: pkgWidth || templateWidth
        }, isPackagingMeasurementMetric),
        ...rest,
        weightLb: this.convertWeight(weightLb, isWeightMeasurementMetric),
        qty: 1,
        insuredValue
      }]
    },
    loadPackagesFromNewFormat (template) {
      const {packages, isMeasurementMetric: isWeightMeasurementMetric = false} = template
      return packages.map(pkg => ({
        ...pkg,
        weightLb: this.convertWeight(pkg.weight || 0, isWeightMeasurementMetric),
        qty: pkg.qty || 1
      }))
    },
    onSelectTemplate (e) {
      let {
        shippingService,
        signature,
        memo = '',
        labelStockType,
        packages
      } = e
      shippingService = this.autoGetRate ? this.shippingService : shippingService
      let replacedMemo = this.flexibleVars.reduce((acc, variable) => {
        return acc.replace(new RegExp(`#{${variable}}`, 'g'), this[variable] || '')
      }, memo)
      Object.assign(this, {
        shippingService,
        memo: replacedMemo,
        signature,
        labelStockType: labelStockType || 'STOCK_4X6'
      })
      this.setShippingService(shippingService)

      if (packages && Array.isArray(packages) && packages.length > 0) {
        const normalizedPackages = this.loadPackagesFromNewFormat(e)
        this.$emit('update:packages', normalizedPackages)
      } else {
        const normalizedPackages = this.loadPackagesFromOldFormat(e)
        this.$emit('update:packages', normalizedPackages)
      }

      this.$emit('selectTemplate', e)
    },
    $$extractData () {
      let {
        carrier,
        carriers,
        shipDate,
        shippingService,
        signature,
        memo,
        note,
        isSaturdayDelivery,
        labelStockType
      } = this

      const isFedexMWT = shippingService === 'FEDEX_MWT - MWT_GENERAL'

      if (isFedexMWT) {
        const maxQty = this.serviceSetting.maxPkgQty
        const totalPkgQty = this.packages.reduce((sum, pkg) => {
          sum += pkg.qty
          return sum
        }, 0)
        if (totalPkgQty > maxQty) throw Error(`Package quantity are limited to ${maxQty}.`)
      }

      const targetLocation = this.isSupportHoldAtLocation && this.isHoldAtLocation
        ? this.locations.find(item => item.locationId === this.location)
        : null

      if (this.location && !targetLocation.isSupportSaturday && isSaturdayDelivery) {
        throw Error(this.$t('locationSaturdayPickup'))
      }

      const packages = this.packages
        .map(pkg => {
          const {
            packaging,
            qty,
            id,
            name,
            isPackagingDefault,
            weightLb,
            po,
            inv,
            dept,
            ...rest
          } = pkg
          return isFedexMWT
            ? {...rest, weight: this.isOz ? (weightLb / 16) : weightLb, id, isMeasurementMetric: this.isMeasurementMetric, qty}
            : new Array(qty).fill(1).map(() => {
              return {...rest, weight: this.isOz ? (weightLb / 16) : weightLb, id, isMeasurementMetric: this.isMeasurementMetric}
            })
        })
        .flat()
        .map(({ reference, ...item }, index) => {
          const payload = {...item, sequenceNumber: index + 1}
          if (this.currentShippingService.enableReferences && this.reference) {
            payload[this.reference] = reference
          }
          return payload
        })
      let replacedMemo = this.flexibleVars.reduce((acc, variable) => {
        if (variable !== 'quantity') {
          return acc
        }
        return acc.replace(new RegExp(`#{${variable}}`, 'g'), this.packages.reduce((pkg) => acc + pkg.qty, 0))
      }, memo)
      const params = {
        packages,
        carrier,
        carriers,
        shipDate,
        shippingService,
        signature,
        memo: replacedMemo,
        note,
        reference: this.customOrderId || ''
      }
      // location
      if (this.isSupportHoldAtLocation && this.isHoldAtLocation) {
        params.isHoldAtLocation = true
        params.location = {
          locationId: targetLocation.locationId,
          address: targetLocation.address,
          locationType: targetLocation.locationType
        }
      }
      this.isSupportSaturdayDelivery && (params.isSaturdayDelivery = isSaturdayDelivery)
      this.isSupportLabelStockType && (params.labelStockType = labelStockType)
      if (this.currentShippingService.isOneRate) {
        // const packageWeight = this.fedexPackagings.find(pkg => pkg.value === this.packagingType).weight
        const { width, length, height, weight, ...rest } = params.packages[0]
        params.packages = [rest]
        params.packageType = this.packageType
        params.pricingOption = PRICING_OPTIONS.ONE_RATE
      }
      return params
    },
    setShippingService (value) {
      this.shippingService = value
      this.$emit('update:shippingService', value)
    },
    setCarrier () {
      this.$emit('update:carrier', this.carrier)
    },
    deletePackage (item) {
      this.$emit('update:packages', this.packages.filter(pkg => pkg !== item))
    },
    defaultZero (e, item) {
      if (!e) {
        item.insuredValue = 0
      }
    },
    selectAll () {
      this.carriers = this.carriers.length === this.allCarriers.length
        ? []
        : this.allCarriers.map(item => item)
    },
    arrayLengthRule () {
      return v => (v && v.length > 0) || this.$t('arrayLengthRule')
    }
  },
  props: {
    packages: Array,
    noteMemoSignature: {
      type: Object,
      default: () => {}
    },
    initIsSaturdayDelivery: {
      type: Boolean,
      default: false
    },
    initLabelStockType: {
      type: String,
      default: 'STOCK_4X6'
    },
    fromState: String,
    autoGetRateComplete: Boolean,
    order: Object,
    dangerousGoods: {
      type: String,
      default: ''
    },
    to: {
      type: Object,
      default: () => ({})
    }
  }
}
</script>

<style>
.table-item {
  padding-bottom: 12px;
}

.location-items .v-list__tile {
  display: block;
  height: auto;
}
</style>



// WEBPACK FOOTER //
// src/components/LabelPackageWidget.vue